import { ComponentChildren } from 'preact';
import { useCallback, useEffect, useRef } from 'preact/hooks';
import { Button } from '../../components/Button/Button';
import { PlaybackSpeedButton } from '../../components/buttons/PlaybackSpeedButton';
import { TogglePlayButton } from '../../components/buttons/TogglePlayButton';
import { ProgressBar } from '../../components/ProgressBar/ProgressBar';
import { AdTimeDisplay } from '../../components/TimeDisplay/AdTimeDisplay';
import { TimeDisplay } from '../../components/TimeDisplay/TimeDisplay';
import { Typography } from '../../components/Typography/Typography';
import { VolumeButton } from '../../components/VolumeButton/VolumeButton';
import { useConfig } from '../../hooks/ConfigProvider';
import { useErrorRecovery } from '../../hooks/ErrorRecoveryProvider';
import { usePlayerError } from '../../hooks/PlayerErrorProvider';
import { useAdData } from '../../hooks/useAdData';
import { usePlayerInteraction } from '../../hooks/usePlayerInteraction';
import { usePlayerMute } from '../../hooks/usePlayerMute';
import { useStreamType } from '../../hooks/useStreamType';
import { useTracker } from '../../hooks/useTracker';
import { useCurrentAsset } from '../../hooks/CurrentAssetProvider';
import { useTranslation } from '../../../../utils/useTranslation';
import ns from '../../translations';

interface AudioPlayerProps {
  actions?: ComponentChildren;
  footer?: ComponentChildren;
}

export const AudioPlayer = ({ actions, footer }: AudioPlayerProps) => {
  const layoutRef = useRef<HTMLDivElement>(null!);
  const isVisibleRef = useRef(true);
  const unmuteButtonRef = useRef<HTMLDivElement>(null);
  const { isMuted } = usePlayerMute();
  const { ui } = useConfig();
  const { adData, isAdLoaded } = useAdData(undefined);
  const isAdPlaying = !!(adData || isAdLoaded);
  const { asset } = useCurrentAsset();
  const isLive = asset?.streamType === 'live';
  const { canSeek } = useStreamType();

  const { errorDetails } = usePlayerError();
  const { isAutoRetrying, handleRetry, handleReload, canRecover } =
    useErrorRecovery();
  const { t } = useTranslation(ns);
  const { trackErrorView } = useTracker();

  useEffect(() => {
    if (errorDetails?.code) {
      trackErrorView(errorDetails.code);
    }
  }, [errorDetails?.code, trackErrorView]);

  const isInteractionEventSupported = useCallback(
    (e: TouchEvent | MouseEvent | FocusEvent | WheelEvent) => {
      if (
        e.target instanceof Node &&
        unmuteButtonRef.current?.contains(e.target)
      ) {
        return false;
      }

      if (
        e.type === 'mousemove' &&
        !isVisibleRef.current &&
        isMuted &&
        ui.unmuteButtonEnabled
      ) {
        return false;
      }

      return true;
    },
    [isMuted, ui.unmuteButtonEnabled]
  );

  const {
    disableTimeout: disableInteractionTimeout,
    enableTimeout: enableInteractionTimeout,
  } = usePlayerInteraction({
    layoutRef,
    isEventSupported: isInteractionEventSupported,
  });

  const handleProgressBarInteraction = useCallback(
    (isProgressBarActive: boolean) => {
      if (isProgressBarActive) {
        disableInteractionTimeout();
      } else {
        enableInteractionTimeout();
      }
    },
    [disableInteractionTimeout, enableInteractionTimeout]
  );

  if (errorDetails) {
    if (isAutoRetrying) {
      return (
        <div className="vgp-audio-player vgp-audio-player-error">
          <Typography variant="label" size="medium">
            {t('Reconnecting...')}
          </Typography>
        </div>
      );
    }

    return (
      <div className="vgp-audio-player vgp-audio-player-error">
        <div className="vgp-audio-player-error-content">
          <Typography variant="label" size="medium" bold>
            {t('Something went wrong', {}, { noJSX: true })}
          </Typography>
          {errorDetails.message ? (
            <Typography variant="label" size="small">
              <span
                dangerouslySetInnerHTML={{ __html: errorDetails.message }}
              />
            </Typography>
          ) : null}
          {errorDetails.code ? (
            <Typography variant="label" size="small">
              {t('Error code')}: {errorDetails.code}
              {errorDetails.referenceId
                ? ` · ${t('Reference ID')}: ${errorDetails.referenceId}`
                : ''}
            </Typography>
          ) : null}
        </div>
        {canRecover ? (
          <Button size="small" onClick={handleRetry}>
            {t('Try again')}
          </Button>
        ) : (
          <Button size="small" onClick={handleReload}>
            {t('Reload')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div ref={layoutRef} className="vgp-audio-player">
      <div className="vgp-audio-player-container">
        <div className="vgp-audio-player-play">
          <TogglePlayButton size="medium" color="primary" />
        </div>
        {canSeek && (
          <div className={'vgp-audio-player-progress-bar-container'}>
            <div className="vgp-audio-player-time">
              {isAdPlaying && <AdTimeDisplay color="primary" />}
            </div>
            <ProgressBar
              className="vgp-audio-player-progress-bar"
              onInteraction={handleProgressBarInteraction}
            />
          </div>
        )}
        <div className="vgp-audio-player-controls">
          <Button
            className={'vgp-audio-player-volume-container'}
            color="secondary"
            size="xsmall"
          >
            <VolumeButton className="vgp-audio-player-volume-button" />
          </Button>
          {!isAdPlaying && !isLive && (
            <PlaybackSpeedButton
              size="xsmall"
              color="secondary"
              className="vgp-audio-player-speed-button"
            />
          )}
          {actions}
        </div>
      </div>
      {footer}
    </div>
  );
};
